"use client"

import * as React from "react"
import { cn } from "../utils"
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "./Command"

type VariableOption = {
  value: string
  label: string
}

type VariableAutocompleteProps = {
  children: React.ReactElement<HTMLInputElement | HTMLTextAreaElement>
  variables: VariableOption[]
  triggerPattern?: string
  className?: string
  disabled?: boolean
  noSpaces?: boolean
}

function findTriggerContext(value: string, cursorPosition: number, triggerPattern: string) {
  const textBeforeCursor = value.slice(0, cursorPosition)
  const textAfterCursor = value.slice(cursorPosition)

  // Look for the last "{{" before cursor
  const lastTriggerIndex = textBeforeCursor.lastIndexOf(triggerPattern)

  if (lastTriggerIndex === -1) return null

  // Check if cursor is right after the first "{" (between "{" and "{")
  // Pattern: "...{[HERE]{..."
  if (cursorPosition === lastTriggerIndex + 1) {
    return null
  }

  // Check if cursor is right before the last "}" (between "}" and "}")
  // Pattern: "...}[HERE]}..."
  const nextChars = textAfterCursor.slice(0, 2)
  if (nextChars === "}}" || (nextChars.startsWith("}") && textBeforeCursor.endsWith("}"))) {
    return null
  }

  // Get text between "{{" and cursor
  const textAfterTrigger = textBeforeCursor.slice(lastTriggerIndex + triggerPattern.length)

  // Check if there's a closing "}}" between the trigger and cursor
  // If yes, we're not inside an open variable block
  if (textAfterTrigger.includes("}}")) {
    return null
  }

  // Check if there's a closing "}}" after the cursor position
  // This means we're inside an existing {{ }} block
  const hasClosingBracesAfter = textAfterCursor.includes("}}")

  // Also check if there's another "{{" after the cursor (which would mean we're in a different context)
  const hasOpeningBracesAfter = textAfterCursor.includes("{{")

  // If there's no closing braces after and no opening braces after, we're inside a variable
  // If there's a closing brace after (and before any opening brace), we're also inside a variable
  if (!hasClosingBracesAfter && hasOpeningBracesAfter) {
    // There's another variable opening after cursor but no closing for current
    // This shouldn't happen in valid syntax but handle it
    return null
  }

  // Trim leading space from search query
  const searchQuery = textAfterTrigger.trimStart()

  return {
    triggerIndex: lastTriggerIndex,
    searchQuery: searchQuery,
  }
}

export function VariableAutocomplete({
  children,
  variables,
  triggerPattern = "{{",
  className,
  disabled = false,
  noSpaces = false,
}: VariableAutocompleteProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [context, setContext] = React.useState<{
    triggerIndex: number
    searchQuery: string
  } | null>(null)
  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const [dropdownPosition, setDropdownPosition] = React.useState({ top: 0, left: 0 })
  const inputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const commandListRef = React.useRef<HTMLDivElement>(null)
  const selectedItemRef = React.useRef<HTMLDivElement>(null)

  const filteredVariables = React.useMemo(() => {
    if (!context) return variables
    const query = context.searchQuery.toLowerCase()
    return variables.filter(
      (v) => v.value.toLowerCase().includes(query) || v.label.toLowerCase().includes(query),
    )
  }, [variables, context])

  const calculateDropdownPosition = React.useCallback(
    (element: HTMLInputElement | HTMLTextAreaElement) => {
      const selectionStart = element.selectionStart ?? 0

      const computedStyle = window.getComputedStyle(element)
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0
      const paddingTop = parseFloat(computedStyle.paddingTop) || 0
      const lineHeight = parseFloat(computedStyle.lineHeight) || 20

      const value = element.value.slice(0, selectionStart)
      const lines = value.split("\n")
      const currentLineIndex = lines.length - 1
      const currentLineText = lines[currentLineIndex] || ""

      const canvas = document.createElement("canvas")
      const ctx = canvas.getContext("2d")
      let textWidth = 0

      if (ctx) {
        ctx.font = computedStyle.font
        textWidth = ctx.measureText(currentLineText).width
      } else {
        textWidth = currentLineText.length * (parseFloat(computedStyle.fontSize) || 16) * 0.6
      }

      const top = paddingTop + currentLineIndex * lineHeight + lineHeight + 4
      const left = Math.min(paddingLeft + textWidth, element.clientWidth - 200)

      return { top, left }
    },
    [],
  )

  const checkForTrigger = React.useCallback(() => {
    if (disabled) return

    const input = inputRef.current
    if (!input) return

    const { value, selectionStart } = input
    if (selectionStart === null) return

    const triggerContext = findTriggerContext(value, selectionStart, triggerPattern)

    if (triggerContext) {
      const pos = calculateDropdownPosition(input)
      setDropdownPosition(pos)
      setContext(triggerContext)
      setIsOpen(true)
      setSelectedIndex(0)
    } else {
      setIsOpen(false)
      setContext(null)
    }
  }, [disabled, triggerPattern, calculateDropdownPosition])

  const closeDropdown = React.useCallback(() => {
    setIsOpen(false)
    setContext(null)
  }, [])

  const insertVariable = React.useCallback(
    (variableValue: string) => {
      if (!inputRef.current || !context) return

      const input = inputRef.current
      const { value, selectionStart } = input

      if (selectionStart === null) return

      const beforeTrigger = value.slice(0, context.triggerIndex)
      const afterCursor = value.slice(selectionStart)

      // Check if there's already a closing "}}" after the cursor
      const hasClosingBracesAfter = afterCursor.trimStart().startsWith("}}")

      // If there's already closing braces, don't add them again
      let newValue: string
      let newCursorPosition: number
      const variable = `{{ ${variableValue} }}`.replace(noSpaces ? /\s/g : "", "")
      const extraCursorPosition = noSpaces ? 4 : 6

      if (hasClosingBracesAfter) {
        // Replace the content between {{ and }}
        const afterClosingBrace = afterCursor.slice(afterCursor.indexOf("}}") + 2)
        newValue = `${beforeTrigger}${variable}${afterClosingBrace}`
        newCursorPosition = context.triggerIndex + variableValue.length + extraCursorPosition
      } else {
        // Normal case: add the variable and closing braces
        newValue = `${beforeTrigger}${variable}${afterCursor}`
        newCursorPosition = context.triggerIndex + variableValue.length + extraCursorPosition
      }

      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        "value",
      )?.set

      const nativeTextareaValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype,
        "value",
      )?.set

      const nativeSetter =
        input.tagName === "INPUT" ? nativeInputValueSetter : nativeTextareaValueSetter

      if (nativeSetter) {
        nativeSetter.call(input, newValue)
      } else {
        input.value = newValue
      }

      input.setSelectionRange(newCursorPosition, newCursorPosition)

      input.dispatchEvent(new Event("input", { bubbles: true }))

      setIsOpen(false)
      setContext(null)

      setTimeout(() => {
        input.focus()
      }, 0)
    },
    [context],
  )

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (disabled || !isOpen) return

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault()
          setSelectedIndex((prev) => {
            const newIndex = prev < filteredVariables.length - 1 ? prev + 1 : prev
            return newIndex
          })
          break
        case "ArrowUp":
          event.preventDefault()
          setSelectedIndex((prev) => {
            const newIndex = prev > 0 ? prev - 1 : 0
            return newIndex
          })
          break
        case "Enter":
          event.preventDefault()
          if (filteredVariables[selectedIndex]) {
            insertVariable(filteredVariables[selectedIndex].value)
          }
          break
        case "Escape":
          event.preventDefault()
          closeDropdown()
          break
        case "Tab":
          event.preventDefault()
          if (filteredVariables[selectedIndex]) {
            insertVariable(filteredVariables[selectedIndex].value)
          }
          break
      }
    },
    [disabled, isOpen, filteredVariables, selectedIndex, insertVariable, closeDropdown],
  )

  // Close dropdown when disabled
  React.useEffect(() => {
    if (disabled && isOpen) {
      closeDropdown()
    }
  }, [disabled, isOpen, closeDropdown])

  // Scroll selected item into view
  React.useEffect(() => {
    if (isOpen && selectedItemRef.current && commandListRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      })
    }
  }, [selectedIndex, isOpen])

  // Handle clicks outside the component to close the dropdown
  React.useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current) return

      const target = event.target as Node
      if (!containerRef.current.contains(target)) {
        closeDropdown()
      }
    }

    const handleFocusOutside = (event: FocusEvent) => {
      if (!containerRef.current) return

      const target = event.relatedTarget as Node | null
      if (target && !containerRef.current.contains(target)) {
        closeDropdown()
      }
    }

    // Use capture phase to catch clicks before they bubble
    document.addEventListener("mousedown", handleClickOutside, true)
    document.addEventListener("focusin", handleFocusOutside)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true)
      document.removeEventListener("focusin", handleFocusOutside)
    }
  }, [isOpen, closeDropdown])

  const child = React.Children.only(children)
  const childRef = (child.props as { ref?: React.Ref<HTMLInputElement | HTMLTextAreaElement> }).ref

  const combinedRef = React.useCallback(
    (node: HTMLInputElement | HTMLTextAreaElement | null) => {
      inputRef.current = node
      if (typeof childRef === "function") {
        childRef(node)
      } else if (childRef && "current" in childRef) {
        ;(childRef as React.MutableRefObject<typeof node>).current = node
      }
    },
    [childRef],
  )

  const childProps = child.props as unknown as Record<string, unknown>
  const originalOnChange = childProps.onChange as
    | React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined
  const originalOnKeyDown = childProps.onKeyDown as
    | React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined
  const originalOnClick = childProps.onClick as
    | React.MouseEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined
  const originalOnSelect = childProps.onSelect as
    | React.ReactEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined
  const originalOnBlur = childProps.onBlur as
    | React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      originalOnChange?.(event)

      requestAnimationFrame(() => {
        checkForTrigger()
      })
    },
    [originalOnChange, checkForTrigger],
  )

  const handleKeyDownWrapped = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleKeyDown(event)
      originalOnKeyDown?.(event)
    },
    [handleKeyDown, originalOnKeyDown],
  )

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      originalOnClick?.(event)

      requestAnimationFrame(() => {
        checkForTrigger()
      })
    },
    [originalOnClick, checkForTrigger],
  )

  const handleSelect = React.useCallback(
    (event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      originalOnSelect?.(event)

      requestAnimationFrame(() => {
        checkForTrigger()
      })
    },
    [originalOnSelect, checkForTrigger],
  )

  const handleBlur = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      originalOnBlur?.(event)

      // Small delay to allow clicks on dropdown items to register first
      setTimeout(() => {
        const activeElement = document.activeElement
        if (
          activeElement &&
          containerRef.current &&
          !containerRef.current.contains(activeElement)
        ) {
          closeDropdown()
        }
      }, 150)
    },
    [originalOnBlur, closeDropdown],
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clonedChild = React.cloneElement(child as React.ReactElement<any>, {
    ref: combinedRef,
    onChange: handleChange,
    onKeyDown: handleKeyDownWrapped,
    onClick: handleClick,
    onSelect: handleSelect,
    onBlur: handleBlur,
    "aria-autocomplete": "list",
    "aria-controls": isOpen ? "variable-autocomplete-listbox" : undefined,
    "aria-expanded": isOpen,
    role: "combobox",
    "aria-haspopup": "listbox",
  })

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {clonedChild}
      {isOpen && !disabled && (
        <div
          id="variable-autocomplete-listbox"
          className="absolute z-50 w-64 rounded-md border bg-popover shadow-md"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
          }}
          role="listbox"
          aria-label="Available variables"
        >
          <Command className="bg-popover">
            <CommandList
              ref={commandListRef}
              className="max-h-[200px] overflow-y-auto"
              role="presentation"
            >
              {filteredVariables.length === 0 ? (
                <CommandEmpty className="py-2 text-center text-sm text-muted-foreground">
                  No variables found
                </CommandEmpty>
              ) : (
                <CommandGroup role="group" aria-label="Variable suggestions">
                  {filteredVariables.map((variable, index) => (
                    <CommandItem
                      key={variable.value}
                      data-index={index}
                      value={variable.value.replace(/"/g, "'")}
                      onSelect={() => insertVariable(variable.value)}
                      ref={index === selectedIndex ? selectedItemRef : null}
                      role="option"
                      aria-selected={index === selectedIndex}
                      id={`variable-option-${index}`}
                      className={cn(
                        "cursor-pointer px-2 py-1.5 outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        index === selectedIndex && "bg-accent",
                      )}
                      tabIndex={-1}
                    >
                      <span className="font-medium">{variable.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  )
}
